/**
 * INTERNAL AUDIT — the flags this card is named for, and the retention clock.
 *
 * Ledger row H4's remainder was one sentence: "the Portal screen is not built"
 * for the retention runs. Building it opened this card, and the card had a
 * bigger hole than the one being closed.
 *
 * MEASURED BEFORE ANY OF THIS WAS WRITTEN, and then run as real signed-in seats
 * rather than read off the policy:
 *
 *   the seat that HOLDS view_admin_flags read   0 of 19 open flags
 *   the single 'all' holder read               19 of 19
 *
 * admin_flags had one RLS policy, gating on 'all' — held by 1 of 6 active
 * staff. Nineteen open flags, written by seven producers, read by no function
 * at all. The permission named for the table granted nothing on it. That is
 * H2's shape in a new place, and script 217 is the route: a SECURITY DEFINER
 * read that logs against view_admin_flags before it resolves a single row.
 *
 * WHY THE REASON IS COLLECTED HERE AND NOT BY ReasonGate.
 *
 * ReasonGate logs through fn_log_my_admin_action and then lets a component
 * fetch. That is right for a read that takes no reason of its own.
 * fn_admin_flags takes one as a required argument and logs it itself, before
 * the first row — so routing this through ReasonGate would write the same
 * disclosure to the audit log twice. ProviderApplications made the same call
 * for the same reason: it collects the reason inline and hands it to
 * fn_provider_application. This follows it.
 *
 * TWO PANELS, AND THE CLOCK COMES FIRST.
 *
 * The flags need a reason and the clock does not, so the clock is the half a
 * desk can see on opening the section. That ordering is also the honest one:
 * "is the platform doing what it says" is answered by the clock in one line,
 * and the flag queue is the work.
 *
 * WHAT THIS SCREEN REFUSES TO DO. It cannot resolve or dismiss a flag.
 * admin_flags has four columns waiting for that and no function in the
 * database writes them — that is the write half and it belongs to Portal P5,
 * where reason capture and the OID chain are specified. The screen says so
 * rather than leaving a desk hunting for a button that was never built.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  O, SANS, Card, StatusPill, LoadingPanel, Notice, PrimaryCTA, QuietButton,
} from '../ui';
import {
  fetchAdminFlags, fetchRetentionClock, fetchRetentionRuns,
  clockVerdict, FLAG_TYPES, FLAG_STATUSES, MIN_FLAG_REASON,
} from '../lib/internalAudit';

const when = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 16).replace('T', ' ');
};

const STATUS_TONE = {
  open: 'warning',
  investigating: 'warning',
  resolved: 'green',
  dismissed: 'quiet',
};

// ---------------------------------------------------------------------------

/**
 * THE CLOCK. POPIA §14 does not say "have a function capable of deleting"; it
 * says do not keep the record longer than you need it. So the question this
 * panel answers is not "what did the job do" but "is the job running at all",
 * and it answers the second before the first.
 */
function RetentionClock() {
  const [clock, setClock] = useState(undefined);
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [showRuns, setShowRuns] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchRetentionClock().then((res) => {
      if (!alive) return;
      if (res.error) setError(res.error);
      else setClock(res.data);
    });
    return () => { alive = false; };
  }, []);

  const openRuns = async () => {
    setShowRuns(true);
    if (runs === null) {
      const res = await fetchRetentionRuns(20);
      setRuns(res.error ? [] : res.data);
      if (res.error) setError(res.error);
    }
  };

  if (error) return <Notice tone="critical">{error}</Notice>;
  if (clock === undefined) return <LoadingPanel message="Reading the retention clock…" />;

  const v = clockVerdict(clock);

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, marginBottom: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 5px',
          }}>Retention clock · POPIA §14</p>
          <p style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: O.ink, margin: 0 }}>
            {v.headline}
          </p>
        </div>
        {/* Never colour alone: the word carries the state and the pill carries
            the word. */}
        <StatusPill tone={v.tone}>
          {clock.is_scheduled ? 'Scheduled' : 'Not scheduled'}
        </StatusPill>
      </div>

      {v.detail && (
        <p style={{
          fontFamily: SANS, fontSize: 12.5, lineHeight: 1.65, color: O.inkMuted, margin: '0 0 12px',
        }}>{v.detail}</p>
      )}

      <QuietButton onClick={openRuns}>
        {showRuns ? 'The runs' : 'Show the runs'}
      </QuietButton>

      {showRuns && (
        runs === null ? <div style={{ marginTop: 12 }}><LoadingPanel message="Loading the runs…" /></div>
          : runs.length === 0 ? (
            <div style={{ marginTop: 12 }}>
              <Notice tone={clock.is_scheduled ? 'info' : 'critical'}>
                {clock.is_scheduled
                  ? 'No runs recorded yet.'
                  : 'No runs recorded — and the job is not scheduled, so there is nothing to have run.'}
              </Notice>
            </div>
          ) : (
            <div style={{ marginTop: 12, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: SANS, fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Started', 'Finished', 'Closed', 'Anonymised', 'Error'].map((h) => (
                      <th key={h} style={{
                        textAlign: 'left', padding: '6px 10px 6px 0', color: O.inkFaint,
                        fontWeight: 700, fontSize: 11, letterSpacing: '0.06em',
                        textTransform: 'uppercase', borderBottom: `1px solid ${O.hairline}`,
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => (
                    <tr key={`${r.started_at}-${i}`}>
                      <td style={{ padding: '7px 10px 7px 0', color: O.ink, whiteSpace: 'nowrap' }}>
                        {when(r.started_at)}
                      </td>
                      <td style={{ padding: '7px 10px 7px 0', color: O.inkMuted, whiteSpace: 'nowrap' }}>
                        {when(r.finished_at) || '—'}
                      </td>
                      <td style={{ padding: '7px 10px 7px 0', color: O.ink }}>{r.closed_count}</td>
                      <td style={{ padding: '7px 10px 7px 0', color: O.ink }}>{r.anonymised_count}</td>
                      <td style={{ padding: '7px 10px 7px 0', color: r.error_text ? O.critical : O.inkFaint }}>
                        {r.error_text || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

/** One flag. A Connect Code, never a name — Prohibition 6. */
function Flag({ row }) {
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, marginBottom: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 5px',
          }}>{row.flag_source === 'automated' ? 'Raised automatically' : `Raised by ${row.flag_source}`}</p>
          <p style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
            {row.flag_type.replace(/_/g, ' ')}
          </p>
        </div>
        <StatusPill tone={STATUS_TONE[row.status] || 'quiet'}>
          {row.status.replace(/_/g, ' ')}
        </StatusPill>
      </div>

      <p style={{
        fontFamily: SANS, fontSize: 12.5, lineHeight: 1.65, color: O.ink, margin: '0 0 8px',
      }}>{row.description}</p>

      {FLAG_TYPES[row.flag_type] && (
        <p style={{
          fontFamily: SANS, fontSize: 11.5, lineHeight: 1.6, color: O.inkMuted, margin: '0 0 10px',
        }}>{FLAG_TYPES[row.flag_type]}</p>
      )}

      <div style={{ borderTop: `1px solid ${O.hairline}`, paddingTop: 8, fontFamily: SANS, fontSize: 12 }}>
        {/* The code and not the name. A desk that needs the person takes this
            to the subject lens, which logs the disclosure on its own. */}
        <div style={{ display: 'flex', gap: 14, padding: '5px 0' }}>
          <span style={{ width: 118, flexShrink: 0, color: O.inkMuted }}>Subject</span>
          <span style={{ color: O.ink }}>{row.subject_code || 'No subject on this flag'}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, padding: '5px 0' }}>
          <span style={{ width: 118, flexShrink: 0, color: O.inkMuted }}>Opened</span>
          <span style={{ color: O.ink }}>{when(row.opened_at)}</span>
        </div>
        {row.reference_table && (
          <div style={{ display: 'flex', gap: 14, padding: '5px 0' }}>
            <span style={{ width: 118, flexShrink: 0, color: O.inkMuted }}>Points at</span>
            <span style={{ color: O.ink }}>{row.reference_table}</span>
          </div>
        )}
        {row.resolution_note && (
          <div style={{ display: 'flex', gap: 14, padding: '5px 0' }}>
            <span style={{ width: 118, flexShrink: 0, color: O.inkMuted }}>Resolution</span>
            <span style={{ color: O.ink }}>{row.resolution_note}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * The reason, before the record. Collected here and handed to the database
 * function, which writes the log entry before it resolves the first flag.
 */
function ReasonPrompt({ busy, error, onConfirm }) {
  const [reason, setReason] = useState('');
  const tooShort = reason.trim().length < MIN_FLAG_REASON;

  return (
    <Card>
      <h2 style={{ fontFamily: SANS, fontSize: 15.5, fontWeight: 700, color: O.ink, margin: 0 }}>
        Why are you opening the flags?
      </h2>
      <p style={{ marginTop: 7, fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
        A flag names a subject and an allegation about them. Your reason is written to the
        append-only log before a single flag is read, and it cannot be edited afterwards.
      </p>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        aria-label="Reason"
        style={{
          width: '100%', boxSizing: 'border-box', marginTop: 14, resize: 'none',
          padding: '12px 13px', borderRadius: 11, fontFamily: SANS, fontSize: 13.5,
          border: `1px solid ${O.hairlineOn}`, color: O.ink, background: '#FFFFFF',
        }}
      />
      {tooShort && reason.length > 0 && (
        <p style={{ marginTop: 6, fontFamily: SANS, fontSize: 11.5, color: O.inkFaint }}>
          A few more words — at least {MIN_FLAG_REASON} characters.
        </p>
      )}

      {error && <div style={{ marginTop: 12 }}><Notice tone="critical">{error}</Notice></div>}

      <div style={{ marginTop: 16 }}>
        <PrimaryCTA onClick={() => onConfirm(reason.trim())} busy={busy} disabled={busy || tooShort}>
          Open the flags
        </PrimaryCTA>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export default function InternalAudit() {
  const [reason, setReason] = useState(null);
  const [rows, setRows] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showing, setShowing] = useState('open');

  /**
   * ONE READ PER DISCLOSURE, AND THIS REF IS WHY IT EXISTS.
   *
   * The first version fetched in `confirm` and again in an effect keyed on
   * [reason, showing] — which fires the moment confirm sets the reason. Two
   * calls for one click, and because fn_admin_flags writes an
   * admin_activity_log row before it reads, that is TWO audit entries for one
   * act of looking. An audit log that double-counts is worse than one that
   * undercounts: it makes a desk look like it opened the flags twice as often
   * as it did.
   *
   * Found by a test that could not see the queue render, not by reading this.
   * The effect now only reloads when the STATUS FILTER changes after the queue
   * is already open; the ref records what has been loaded.
   */
  const loaded = useRef(null);

  const load = useCallback(async (why, status) => {
    loaded.current = status;
    setRows(undefined);
    setError(null);
    const res = await fetchAdminFlags(why, status);
    if (res.error) { setError(res.error); setRows([]); return; }
    setRows(res.data);
  }, []);

  useEffect(() => {
    if (!reason) return;
    if (loaded.current === showing) return;
    load(reason, showing);
  }, [reason, showing, load]);

  const confirm = async (why) => {
    setBusy(true);
    setError(null);
    const res = await fetchAdminFlags(why, 'open');
    setBusy(false);
    // The refusal is the database's, in its own words. If it declines — no
    // permission, a reason too short — the reason is not accepted and the
    // queue does not open.
    if (res.error) { setError(res.error); return; }
    loaded.current = 'open';
    setReason(why);
    setRows(res.data);
  };

  return (
    <div>
      {/* The clock is reason-free and it is the one-line answer to "is the
          platform doing what it says", so it renders whether or not the flags
          have been opened. */}
      <RetentionClock />

      {!reason ? (
        <ReasonPrompt busy={busy} error={error} onConfirm={confirm} />
      ) : (
        <>
          <p style={{
            fontFamily: SANS, fontSize: 12.5, color: O.inkMuted,
            margin: '0 0 16px', lineHeight: 1.7, maxWidth: 620,
          }}>
            Seven different parts of the platform raise a flag when something does not add up.
            Until now nothing read them: the table was reachable only by an account holding
            every permission, so these sat unseen. Oldest first — the one that has waited
            longest is the one the platform has been ignoring longest.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 18 }}>
            {FLAG_STATUSES.map((s) => {
              const on = showing === s.key;
              return (
                <div key={s.key} style={{ flex: '1 1 0', minWidth: 96, display: 'flex' }}>
                  {on
                    ? <PrimaryCTA onClick={() => setShowing(s.key)} style={{ width: '100%' }}>{s.label}</PrimaryCTA>
                    : <QuietButton onClick={() => setShowing(s.key)} style={{ width: '100%', justifyContent: 'center' }}>{s.label}</QuietButton>}
                </div>
              );
            })}
          </div>

          {error && <Notice tone="critical">{error}</Notice>}

          {rows === undefined ? (
            <LoadingPanel message="Loading the flags…" />
          ) : rows.length === 0 ? (
            // MDS 2.2: an empty queue is a SUCCESS. It is not filled with anything.
            <Notice tone="success">
              {showing === 'open'
                ? 'Nothing is flagged and open. Everything the platform watches is adding up.'
                : 'Nothing here.'}
            </Notice>
          ) : (
            rows.map((r) => <Flag key={r.flag_id} row={r} />)
          )}

          {/* Said plainly rather than left as a missing button somebody hunts
              for. The columns exist; the function that writes them does not. */}
          <p style={{
            fontFamily: SANS, fontSize: 11.5, lineHeight: 1.65, color: O.inkFaint,
            marginTop: 18, maxWidth: 620,
          }}>
            Resolving a flag is not built yet. The record has somewhere to put a decision
            and nothing in the database writes it, so this desk can read the flags and
            cannot yet close one.
          </p>
        </>
      )}
    </div>
  );
}
